import { useAuthenticationStore } from '@/stores/authentication/AuthenticationStore';
import { useExhibitorStore } from '@/stores/exhibitor/ExhibitorStore';
import { getWhiteLabelState } from '@/stores/storeFacade';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { enrichListing } from '@/utils/listingUtils';
import type { ArcualInventoryItem, ListingFilterOptions, ListingFilters, ResultList } from '@arcual/lib-app-platform';
import { getListingsService } from '@arcual/lib-app-platform';

const listingStoreName = 'listingStore';
export const useListingStore = defineStore(listingStoreName, () => {
  const listings = ref<Record<string, ArcualInventoryItem>>({});
  const randomSeed = ref<string>(Date.now().toString());
  const activationId = computed(() => getWhiteLabelState().activationId.value);

  const getPreviousItemId = (id: string): string => {
    const artworkKeys = Object.keys(listings.value);
    const index = artworkKeys.findIndex(artworkKey => artworkKey === id);
    if (index - 1 >= 0) {
      return artworkKeys[index - 1];
    }

    return '';
  }

  const getNextItemId = (id: string): string => {
    const artworkKeys = Object.keys(listings.value);
    const index = artworkKeys.findIndex(artworkKey => artworkKey === id);
    if (index + 1 < artworkKeys.length) {
      return artworkKeys[index + 1];
    }

    return '';
  }

  const getListing = async (id: string): Promise<ArcualInventoryItem> => {
    let listing = listings.value[id];

    if (listing) {
      return listing;
    }

    listing = await getListingsService().getListing(
      activationId.value,
      id,
      !!useAuthenticationStore().getGuestEmail().value,
    );
    await enrichListing(listing);

    listings.value[id] = listing;
    return listing;
  };

  const postListings = async (
    filters?: ListingFilters,
    includeSeller = true,
  ): Promise<ResultList<ArcualInventoryItem>> => {

    const result = await getListingsService().postListings(activationId.value, {
      ...filters,
      randomSeed: randomSeed.value,
    });

    for (const listing of result.items) {
      if (includeSeller) {
        listing.seller = (await useExhibitorStore().getExhibitor(listing.sellerProfileId)).value;
      }
      listings.value[listing.id] = listing;
    }

    return result;
  };

  const getListingsFilters = async (filters?: Partial<ListingFilters>): Promise<ListingFilterOptions> => {
    return getListingsService().getListingsFilters(activationId.value, filters);
  };

  /**
   * Reset the store to its initial state
   */
  const reset = () => {
    listings.value = {};
    randomSeed.value = Date.now().toString();
  };

  return {
    getListing,
    postListings,
    getListingsFilters,
    getNextItemId,
    getPreviousItemId,
    reset,
  };
});
